<template>
  <div class="teacher-content" :inert="Boolean(studentProfile)">
  <AppShell
    role-label="Teacher"
    :nav-items="navItems"
    :active-view="activeView"
    :account="auth.user || {}"
    :collapsed="navCollapsed"
    @navigate="openView"
    @logout="signOut"
    @toggle-nav="navCollapsed = !navCollapsed"
  >
    <div class="teacher-workspace">
      <TeacherExperimentProgress />
      <PageHeader
        eyebrow="Teacher workspace"
        :title="pageTitle"
        :description="pageDescription"
        :show-back="Boolean(selectedClass)"
        @back="closeClass"
      />

      <div v-if="overviewMessage && !selectedClass && activeView === 'overview'" class="ui-notice teacher-notice" role="status">{{ overviewMessage }}</div>
      <div v-if="message" class="ui-notice teacher-notice" role="status">{{ message }}</div>

      <template v-if="!selectedClass">
        <section v-if="activeView === 'overview'" class="overview-stack">
          <MetricStrip :items="teacherMetrics" />

          <div class="overview-grid">
            <article class="workspace-card attention-card">
              <SectionHeader title="Needs attention" description="Submitted writing records ready for teacher review." />
              <div v-if="reviewQueue.length" class="review-list">
                <div v-for="row in reviewQueue" :key="`${row.student.id}-${row.assignment.id}`" class="review-item">
                  <div>
                    <strong>{{ displayName(row.student) }}</strong>
                    <span>{{ row.assignment.title }}</span>
                  </div>
                  <button type="button" class="ui-button ui-button--primary compact-button" :disabled="!row.id" @click="openRecord(row.id)">View / Grade</button>
                </div>
              </div>
              <div v-else class="ui-empty">No submitted writing is waiting for review.</div>
            </article>

            <article class="workspace-card">
              <SectionHeader title="Writing status" description="Current progress across your students' writing records." />
              <dl class="status-distribution">
                <div v-for="status in statuses" :key="status">
                  <dt><StatusPill :value="status" /></dt>
                  <dd>{{ statusCounts[status] }}</dd>
                </div>
              </dl>
            </article>
          </div>

          <article v-if="upcomingAssignments.length" class="workspace-card">
            <SectionHeader title="Upcoming assignments" description="Active class assignments ordered by their next due time." />
            <div class="table-scroll">
              <table class="ui-table">
                <thead class="thead"><tr><th>Assignment</th><th>Due</th></tr></thead>
                <tbody>
                  <tr v-for="assignment in upcomingAssignments" :key="assignment.id">
                    <td data-label="Assignment"><strong>{{ assignment.title }}</strong></td>
                    <td data-label="Due">{{ formatDate(assignment.due_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </article>
        </section>

        <section v-if="activeView === 'classes'" class="workspace-card">
          <SectionHeader title="Programme/Class" description="Open a class to manage students, published assignments, and grades." />
          <div class="list-toolbar">
            <input v-model="classSearch" class="ui-input" aria-label="Search Programme/Class" placeholder="Search Programme/Class..." />
          </div>
          <div v-if="filteredClasses.length" class="class-grid">
            <button v-for="item in filteredClasses" :key="item.id" type="button" class="class-card" @click="openClass(item.id)">
              <span class="class-card__topline">
                <span class="class-card__programme">{{ item.programme_name || 'Programme not specified' }}</span>
                <StatusPill :value="item.is_active ? 'active' : 'archived'" />
              </span>
              <strong>{{ item.class_code }} · {{ item.class_name }}</strong>
              <span>{{ item.student_count }} students · {{ item.assignment_count }} assignments</span>
            </button>
          </div>
          <div v-else class="ui-empty">No Programme/Class matches this search.</div>
        </section>

        <section v-if="activeView === 'records'" class="workspace-card">
          <SectionHeader title="Writing Records" description="Find student writing by name, assignment, or workflow status." />
          <div class="list-toolbar">
            <input v-model="recordSearch" class="ui-input" aria-label="Search writing records" placeholder="Search writing records..." />
            <select v-model="recordStatus" class="ui-select filter-select" aria-label="Filter writing records by status"><option value="">All statuses</option><option v-for="status in statuses" :key="status" :value="status">{{ labelStatus(status) }}</option></select>
          </div>
          <div v-if="filteredRecords.length" class="table-scroll">
            <table class="ui-table">
              <thead class="thead"><tr><th>Student</th><th>Assignment</th><th>Class</th><th>Status</th><th>Updated</th><th>Action</th></tr></thead>
              <tbody>
                <tr v-for="row in filteredRecords" :key="`${row.student.id}-${row.assignment.id}`">
                  <td data-label="Student"><strong>{{ displayName(row.student) }}</strong></td><td data-label="Assignment">{{ row.assignment.title }}</td><td data-label="Class">{{ row.assignment.programme_class?.class_code || row.student.class_group || '-' }}</td><td data-label="Status"><StatusPill :value="row.status" /></td><td data-label="Updated">{{ formatDate(row.updated_at) }}</td><td data-label="Action"><button type="button" class="ui-button ui-button--primary compact-button" :disabled="!row.id" @click="openRecord(row.id)">View / Grade</button></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="ui-empty">No writing records match these filters.</div>
        </section>

        <section v-if="activeView === 'students'" class="workspace-card">
          <SectionHeader title="Students" description="Open a student profile to review their grade history across assignments." />
          <div class="list-toolbar">
            <input v-model="studentSearch" class="ui-input" aria-label="Search students" placeholder="Search students..." />
          </div>
          <div v-if="filteredStudents.length" class="table-scroll">
            <table class="ui-table">
              <thead class="thead"><tr><th>Student</th><th>Username</th><th>Class</th><th>Action</th></tr></thead>
              <tbody>
                <tr v-for="student in filteredStudents" :key="student.id">
                  <td data-label="Student"><strong>{{ displayName(student) }}</strong></td><td data-label="Username">{{ student.username }}</td><td data-label="Class">{{ student.class_group || '-' }}</td><td data-label="Action"><button type="button" class="ui-button compact-button" @click="openStudentProfile(student)">View profile</button></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="ui-empty">No students match this search.</div>
        </section>

        <section v-if="activeView === 'profile'" class="workspace-card form-card">
          <AccountEmailSettings />
          <SectionHeader title="Profile details" description="Update the name shown across the teacher workspace." />
          <div class="form-grid">
            <FormField label="Name"><input v-model="profileForm.display_name" class="ui-input" /></FormField>
            <FormField label="Gender"><select v-model="profileForm.gender" class="ui-select"><option value="">Not specified</option><option value="female">Female</option><option value="male">Male</option><option value="other">Other</option></select></FormField>
            <FormField label="Username" helper="Usernames are managed by an administrator."><input :value="auth.user?.username" class="ui-input" disabled /></FormField>
            <div class="form-actions"><button type="button" class="ui-button ui-button--primary" @click="saveProfile">Save profile</button></div>
          </div>
        </section>

        <section v-if="activeView === 'passwordRequests'" class="workspace-card">
          <AccountEmailAdminPanel />
          <SectionHeader title="Password reset requests" description="Issue a temporary password only for students in your own class." />
          <div v-if="temporaryPassword" class="ui-notice" role="status">
            Temporary password for <strong>{{ temporaryUsername }}</strong>: <code>{{ temporaryPassword }}</code>. Copy it now; it will not be shown again.
          </div>
          <div v-if="resetRequests.length" class="table-scroll">
            <table class="ui-table">
              <thead><tr><th>Username</th><th>Class note</th><th>Requested</th><th>Status</th><th>Action</th></tr></thead>
              <tbody><tr v-for="item in resetRequests" :key="item.id"><td>{{ item.username }}</td><td>{{ item.class_note || '-' }}</td><td>{{ formatDate(item.created_at) }}</td><td><StatusPill :value="item.status" /></td><td><button class="ui-button ui-button--primary compact-button" type="button" :disabled="item.status !== 'pending'" @click="resolveReset(item)">Issue temporary password</button></td></tr></tbody>
            </table>
          </div>
          <div v-else class="ui-empty">No password reset requests are waiting.</div>
        </section>
      </template>

      <section v-else class="class-workspace">
        <article class="workspace-card class-summary">
          <div>
            <p>{{ selectedClass.programme_name || 'Programme/Class' }}</p>
            <h2>{{ selectedClass.class_code }} · {{ selectedClass.class_name }}</h2>
            <span>{{ selectedClass.students.length }} students · {{ selectedClass.assignments.length }} assignments</span>
          </div>
          <StatusPill :value="selectedClass.is_active ? 'active' : 'archived'" />
        </article>

        <div class="class-tabs" role="tablist" aria-label="Programme/Class sections">
          <button v-for="tab in classTabs" :key="tab" type="button" role="tab" :aria-selected="String(classTab === tab)" :class="{ 'is-active': classTab === tab }" @click="classTab = tab">{{ tab }}</button>
        </div>

        <article v-if="classTab === 'Students'" class="workspace-card">
          <SectionHeader title="Students" description="Current student membership and profile access for this class." />
          <div v-if="selectedClass.students.length" class="table-scroll">
            <table class="ui-table">
              <thead class="thead"><tr><th>Student</th><th>Username</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>
                <tr v-for="student in selectedClass.students" :key="student.id">
                  <td data-label="Student"><strong>{{ displayName(student) }}</strong></td><td data-label="Username">{{ student.username }}</td><td data-label="Status"><StatusPill :value="student.is_active ? 'active' : 'inactive'" /></td><td data-label="Action"><button type="button" class="ui-button compact-button" @click="openStudentProfile(student)">View profile</button></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="ui-empty">No students are enrolled in this class.</div>
        </article>

        <article v-if="classTab === 'Assignments'" class="workspace-card assignments-card">
          <SectionHeader title="Publication history" description="Review published and archived Assignment Bank briefs for this class.">
            <template #actions><button type="button" class="ui-button ui-button--primary" @click="showPublish = !showPublish">{{ showPublish ? 'Cancel' : 'Publish assignment' }}</button></template>
          </SectionHeader>

          <form v-if="showPublish" class="publication-sheet" @submit.prevent="publishAssignment">
            <div class="publication-sheet__intro">
              <div><p>New publication</p><h3>Set the class delivery details</h3></div>
              <span>Assignment Bank content stays administrator-owned.</span>
            </div>
            <div class="form-grid">
              <FormField label="Assignment Bank item" helper="Choose an administrator-owned brief. The source remains read-only." required><select v-model="publishForm.exercise_id" class="ui-select" required><option value="">Select an assignment</option><option v-for="item in templates" :key="item.id" :value="item.id">{{ item.title }}</option></select></FormField>
              <FormField label="Publication title" helper="The class-specific title students will see."><input v-model="publishForm.title" class="ui-input" placeholder="Defaults to the bank title" /></FormField>
              <FormField
                class="form-wide"
                label="Teacher note / Short description"
                helper="Optional class-specific guidance from the teacher. The administrator-authored Task Brief remains unchanged."
              >
                <textarea v-model="publishForm.teacher_note" class="ui-textarea" rows="3" maxlength="500" />
              </FormField>
              <FormField class="form-wide" label="Task Brief" helper="Read-only during publication. Students receive an immutable snapshot of this bank brief."><textarea :value="selectedTemplate?.prompt || ''" class="ui-textarea task-brief" rows="7" disabled /></FormField>
              <FormField label="Start time" helper="Leave blank to make the assignment available immediately."><input v-model="publishForm.start_at" class="ui-input" type="datetime-local" /></FormField>
              <FormField label="Due time" helper="Choose the deadline shown to students."><input v-model="publishForm.due_at" class="ui-input" type="datetime-local" /></FormField>
              <FormField label="Audience" helper="Publish to everyone or only the selected students."><select v-model="publishForm.audience_type" class="ui-select"><option value="whole_class">Whole class</option><option value="selected_students">Selected students</option></select></FormField>
              <FormField class="form-wide" label="Study preparation" helper="Students wait until the administrator selects the study participants. Unselected classmates can then use this as an ordinary assignment."><label><input type="checkbox" v-model="publishForm.experiment_preparation" /> Use for a grouped writing study</label></FormField>
              <FormField v-if="publishForm.audience_type === 'selected_students'" class="form-wide" label="Students" helper="Select every student who should receive this publication."><select v-model="publishForm.student_ids" class="ui-select multi-select" multiple size="6"><option v-for="student in selectedClass.students" :key="student.id" :value="student.id">{{ displayName(student) }} · {{ student.username }}</option></select></FormField>
              <div class="form-actions"><button class="ui-button ui-button--primary" type="submit">Publish</button><button class="ui-button" type="button" @click="showPublish = false">Cancel</button></div>
            </div>
          </form>

          <div v-if="selectedClass.assignments.length" class="table-scroll">
            <table class="ui-table assignments-table">
              <thead class="thead"><tr><th>Assignment</th><th>Bank item</th><th>Teacher note</th><th>Audience</th><th>Start</th><th>Due</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>
                <template v-for="assignment in selectedClass.assignments" :key="assignment.id">
                  <tr>
                    <td data-label="Assignment"><strong>{{ assignment.title }}</strong></td><td data-label="Bank item">{{ assignment.exercise?.title || '-' }}</td><td data-label="Teacher note">{{ assignment.teacher_note || '-' }}</td><td data-label="Audience">{{ assignment.audience_type === 'selected_students' ? `${assignment.assigned_students.length} students` : 'Whole class' }}</td><td data-label="Start">{{ formatDate(assignment.start_at) }}</td><td data-label="Due">{{ formatDate(assignment.due_at) }}</td><td data-label="Status"><StatusPill :value="assignment.publication_status" /></td><td data-label="Action"><button type="button" class="ui-button ui-button--quiet compact-button" @click="openNoteEditor(assignment)">Edit note</button><button v-if="canArchiveAssignment(assignment)" type="button" class="ui-button ui-button--danger compact-button" @click="archiveAssignment(assignment)">Archive</button></td>
                  </tr>
                  <tr v-if="editingNoteId === assignment.id" class="assignment-note-editor">
                    <td colspan="8" data-label="Teacher note editor">
                      <div class="note-editor-controls">
                        <label :for="`teacher-note-${assignment.id}`">Teacher note / Short description</label>
                        <textarea :id="`teacher-note-${assignment.id}`" v-model="noteDraft" class="ui-textarea" rows="3" maxlength="500" />
                        <div class="form-actions"><button type="button" class="ui-button ui-button--primary compact-button" @click="saveTeacherNote">Save</button><button type="button" class="ui-button compact-button" @click="cancelNoteEditor">Cancel</button></div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          <div v-else class="ui-empty">No publication history is available for this class.</div>
        </article>

        <article v-if="classTab === 'Grades'" class="grades-stack">
          <MetricStrip :items="gradeMetrics" />
          <section class="workspace-card">
            <SectionHeader title="Class grades" description="Review writing progress and export the current grade overview." />
            <div class="list-toolbar grades-toolbar">
              <input v-model="gradeSearch" class="ui-input" aria-label="Search student or assignment grades" placeholder="Search student or assignment..." />
              <select v-model="gradeStatus" class="ui-select filter-select" aria-label="Filter grades by status"><option value="">All statuses</option><option v-for="status in statuses" :key="status" :value="status">{{ labelStatus(status) }}</option></select>
              <button type="button" class="ui-button export-button" @click="downloadGrades('csv')">CSV</button>
              <button type="button" class="ui-button export-button" @click="downloadGrades('xlsx')">XLSX</button>
            </div>
            <div v-if="filteredGrades.length" class="table-scroll">
              <table class="ui-table grades-table">
                <thead class="thead"><tr><th>Student</th><th>Assignment</th><th>Status</th><th>AI Grade</th><th>Teacher Grade</th><th>Feedback</th><th>Action</th></tr></thead>
                <tbody>
                  <tr v-for="row in filteredGrades" :key="`${row.student.id}-${row.assignment.id}`">
                    <td data-label="Student"><strong>{{ displayName(row.student) }}</strong></td><td data-label="Assignment">{{ row.assignment.title }}</td><td data-label="Status"><StatusPill :value="row.status" /></td><td data-label="AI Grade">{{ score(row.ai_grade) }}</td><td data-label="Teacher Grade">{{ score(row.teacher_grade) }}</td><td class="truncate feedback-column" data-label="Feedback">{{ row.teacher_feedback || '-' }}</td><td data-label="Action"><button type="button" class="ui-button ui-button--primary compact-button" :disabled="!row.record_id" @click="openRecord(row.record_id)">View / Grade</button></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="ui-empty">No class grade records match these filters.</div>
          </section>
        </article>

      </section>
    </div>
  </AppShell>
  </div>
  <StudentGradesDialog v-if="studentProfile" :profile="studentProfile" @close="closeStudentProfile" />
</template>

<script setup>
import TeacherExperimentProgress from '../experiments/TeacherExperimentProgress.vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { apiFetch } from '../api'
import AppShell from '../components/AppShell.vue'
import FormField from '../components/FormField.vue'
import AccountEmailSettings from '../components/AccountEmailSettings.vue'
import AccountEmailAdminPanel from '../components/AccountEmailAdminPanel.vue'
import MetricStrip from '../components/MetricStrip.vue'
import PageHeader from '../components/PageHeader.vue'
import SectionHeader from '../components/SectionHeader.vue'
import StatusPill from '../components/StatusPill.vue'
import StudentGradesDialog from '../components/StudentGradesDialog.vue'
import dashboardHelpers from '../dashboard-helpers'
import { useAuthStore } from '../stores/auth'

const auth = useAuthStore(); const router = useRouter(); const route = useRoute()
const { canArchiveAssignment, requestJson } = dashboardHelpers
const activeView = ref('overview'); const classes = ref([]); const records = ref([]); const students = ref([]); const templates = ref([])
const selectedClass = ref(null); const classTab = ref('Students'); const grades = ref([]); const gradeSummary = ref({}); const message = ref(''); const overviewMessage = ref('')
const overview = ref({
  metrics: { classes: 0, students: 0, open_assignments: 0, awaiting_review: 0 },
  review_queue: [],
  upcoming_assignments: [],
  classes_without_open_assignments: [],
})
const showPublish = ref(false); const studentProfile = ref(null); const studentProfileTrigger = ref(null); const classSearch = ref(''); const recordSearch = ref(''); const recordStatus = ref(''); const studentSearch = ref(''); const gradeSearch = ref(''); const gradeStatus = ref('')
const profileForm = ref({ display_name: '', gender: '' }); const statuses = ['not_started', 'in_progress', 'submitted', 'reviewed']; const classTabs = ['Students', 'Assignments', 'Grades']
const publishForm = ref({ exercise_id: '', title: '', teacher_note: '', start_at: '', due_at: '', audience_type: 'whole_class', student_ids: [], experiment_preparation: false })
const editingNoteId = ref(null); const noteDraft = ref('')
const resetRequests = ref([]); const temporaryPassword = ref(''); const temporaryUsername = ref('')
const navItems = [{ view: 'overview', label: 'Overview' }, { view: 'classes', label: 'Programme/Class' }, { view: 'records', label: 'Writing Records' }, { view: 'students', label: 'Students' }, { view: 'passwordRequests', label: 'Password Requests' }, { view: 'profile', label: 'Profile' }]
const mobileViewport = window.matchMedia('(max-width: 767px)')
const navCollapsed = ref(window.matchMedia('(max-width: 767px)').matches)
const pageTitle = computed(() => selectedClass.value ? `${selectedClass.value.class_code} · ${selectedClass.value.class_name}` : navItems.find(item => item.view === activeView.value)?.label || 'Overview')
const pageDescription = computed(() => selectedClass.value
  ? 'Manage the students, assignments, and grades for this Programme/Class.'
  : ({ overview: 'Review the work that needs attention and monitor class activity.', classes: 'Open a Programme/Class to publish assignments and review grades.', records: 'Search student writing and continue grading.', students: 'Find students and review their grade history.', passwordRequests: 'Help students in your classes recover access.', profile: 'Manage your teacher identity.' })[activeView.value])
const teacherMetrics = computed(() => [
  { label: 'Programme/Classes', value: overview.value.metrics.classes, detail: 'Your current cohorts' },
  { label: 'Students', value: overview.value.metrics.students, detail: 'Across your classes' },
  { label: 'Open assignments', value: overview.value.metrics.open_assignments, detail: 'Published class work' },
  { label: 'Awaiting review', value: overview.value.metrics.awaiting_review, detail: 'Submitted student work' },
])
const statusCounts = computed(() => Object.fromEntries(statuses.map(status => [status, records.value.filter(row => row.status === status).length])))
const reviewQueue = computed(() => overview.value.review_queue)
const upcomingAssignments = computed(() => overview.value.upcoming_assignments)
const gradeMetrics = computed(() => [
  { label: 'Records', value: gradeSummary.value.total || 0, detail: 'Class writing rows' },
  { label: 'Submitted', value: gradeSummary.value.submitted || 0, detail: 'Ready for review' },
  { label: 'Not submitted', value: gradeSummary.value.not_submitted || 0, detail: 'Awaiting students' },
  { label: 'Graded', value: gradeSummary.value.graded || 0, detail: 'Teacher-reviewed' },
])
const selectedTemplate = computed(() => templates.value.find(item => item.id === Number(publishForm.value.exercise_id)))
const filteredClasses = computed(() => classes.value.filter(item => `${item.class_code} ${item.class_name} ${item.programme_name}`.toLowerCase().includes(classSearch.value.toLowerCase())))
const filteredRecords = computed(() => records.value.filter(row => (!recordStatus.value || row.status === recordStatus.value) && `${displayName(row.student)} ${row.assignment.title}`.toLowerCase().includes(recordSearch.value.toLowerCase())))
const filteredStudents = computed(() => students.value.filter(row => `${displayName(row)} ${row.username} ${row.class_group}`.toLowerCase().includes(studentSearch.value.toLowerCase())))
const filteredGrades = computed(() => grades.value.filter(row => (!gradeStatus.value || row.status === gradeStatus.value) && `${displayName(row.student)} ${row.assignment.title}`.toLowerCase().includes(gradeSearch.value.toLowerCase())))

onMounted(async () => { mobileViewport.addEventListener('change', syncNavigationForViewport); const requested = String(route.query.view || ''); if (navItems.some(item => item.view === requested)) activeView.value = requested; await Promise.all([loadOverview(), loadClasses(), loadRecords(), loadStudents(), loadTemplates(), loadProfile()]) })
onBeforeUnmount(() => mobileViewport.removeEventListener('change', syncNavigationForViewport))
function syncNavigationForViewport(event) { navCollapsed.value = event.matches }
async function loadOverview() {
  overviewMessage.value = ''
  const result = await requestJson(apiFetch('overview'), 'Could not load overview.')
  if (!result.ok) {
    overviewMessage.value = result.error
    return
  }
  overview.value = result.data
}
async function loadClasses() { const res = await apiFetch('programme-classes'); classes.value = (await res.json()).programme_classes || [] }
async function loadRecords() { const res = await apiFetch('teacher/records'); records.value = (await res.json()).records || [] }
async function loadStudents() { const res = await apiFetch('teacher/students'); students.value = (await res.json()).students || [] }
async function loadTemplates() { const res = await apiFetch('teacher/assignment-templates'); templates.value = (await res.json()).assignment_bank || [] }
async function loadProfile() { const res = await apiFetch('auth/profile'); if (res.ok) { const data = (await res.json()).profile; profileForm.value = { display_name: data.display_name || '', gender: data.gender || '' } } }
async function openClass(id) { cancelNoteEditor(); const [detailRes, gradeRes] = await Promise.all([apiFetch(`programme-classes/${id}`), apiFetch(`programme-classes/${id}/grades`)]); selectedClass.value = (await detailRes.json()).programme_class; const gradeData = await gradeRes.json(); grades.value = gradeData.grades || []; gradeSummary.value = gradeData.summary || {}; classTab.value = 'Students'; studentProfile.value = null }
function closeClass() { selectedClass.value = null; showPublish.value = false; cancelNoteEditor(); loadClasses() }
function openView(view) { selectedClass.value = null; cancelNoteEditor(); activeView.value = view; if (view === 'passwordRequests') loadResetRequests() }
async function loadResetRequests() { const res = await apiFetch('password-reset-requests'); if (res.ok) resetRequests.value = (await res.json()).requests || [] }
async function resolveReset(item) { if (!window.confirm('This immediately changes the password and signs out existing sessions. Verify the account owner and deliver the temporary password privately. After changing it, the owner can bind their own email. Continue?')) return; const res = await apiFetch(`password-reset-requests/${item.id}/resolve`, { method: 'POST' }); const data = await res.json(); if (!res.ok) return message.value = data.error; temporaryPassword.value = data.temporary_password; temporaryUsername.value = item.username; await loadResetRequests() }
async function publishAssignment() {
  message.value = ''
  const result = await requestJson(apiFetch(`programme-classes/${selectedClass.value.id}/assignments`, { method: 'POST', body: JSON.stringify(publishForm.value) }), 'Could not publish assignment.')
  if (!result.ok) {
    message.value = result.error
    return
  }
  const prepared = publishForm.value.experiment_preparation
  publishForm.value = { exercise_id: '', title: '', teacher_note: '', start_at: '', due_at: '', audience_type: 'whole_class', student_ids: [], experiment_preparation: false }
  showPublish.value = false
  message.value = prepared ? 'Assignment published. Waiting for administrator study setup.' : 'Assignment published.'
  await Promise.all([openClass(selectedClass.value.id), loadOverview()])
  classTab.value = 'Assignments'
}
function openNoteEditor(assignment) { editingNoteId.value = assignment.id; noteDraft.value = assignment.teacher_note || '' }
function cancelNoteEditor() { editingNoteId.value = null; noteDraft.value = '' }
async function saveTeacherNote() {
  const result = await requestJson(apiFetch(`admin/assignments/${editingNoteId.value}`, { method: 'PATCH', body: JSON.stringify({ teacher_note: noteDraft.value }) }), 'Could not update teacher note.')
  if (!result.ok) {
    message.value = result.error
    return
  }
  message.value = 'Teacher note saved.'
  editingNoteId.value = null
  noteDraft.value = ''
  await openClass(selectedClass.value.id)
  classTab.value = 'Assignments'
}
async function archiveAssignment(assignment) { if (!window.confirm(`Archive “${assignment.title}”? Student work and grades will be preserved.`)) return; const res = await apiFetch(`admin/assignments/${assignment.id}`, { method: 'DELETE' }); const data = await res.json(); message.value = res.ok ? `Assignment ${data.action}.` : data.error; await Promise.all([openClass(selectedClass.value.id), loadOverview()]); classTab.value = 'Assignments' }
async function openStudentProfile(student) { const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null; const res = await apiFetch(`students/${student.id}/grades`); if (res.ok) { studentProfileTrigger.value = trigger; studentProfile.value = await res.json() } }
async function closeStudentProfile() { const trigger = studentProfileTrigger.value; studentProfile.value = null; studentProfileTrigger.value = null; await nextTick(); trigger?.focus() }
async function downloadGrades(format) { const res = await apiFetch(`programme-classes/${selectedClass.value.id}/grades?format=${format}`); if (!res.ok) return message.value = (await res.json()).error; const blob = await res.blob(); const url = URL.createObjectURL(blob); const anchor = document.createElement('a'); anchor.href = url; anchor.download = `${selectedClass.value.class_code}-grades.${format}`; anchor.click(); URL.revokeObjectURL(url) }
async function saveProfile() { const res = await apiFetch('auth/profile', { method: 'PATCH', body: JSON.stringify(profileForm.value) }); const data = await res.json(); message.value = res.ok ? 'Profile saved.' : data.error }
function openRecord(id) { if (id) router.push(`/teacher/records/${id}`) }
async function signOut() { await auth.logout(); router.replace('/login') }
function displayName(user) { return user?.display_name || user?.username || '-' }
function labelStatus(value) { return ({ not_started: 'Not started', in_progress: 'In progress', submitted: 'Submitted', reviewed: 'Reviewed' })[value] || value || '-' }
function formatDate(value) { if (!value) return '-'; return new Date(value).toLocaleString() }
function score(value) { return value == null ? 'Not graded' : `${value} / 4` }
</script>

<style scoped>
.teacher-workspace {
  width: min(100%, 92rem);
  margin: 0 auto;
}

.teacher-notice {
  margin-bottom: var(--space-6);
}

.overview-stack,
.class-workspace,
.grades-stack {
  display: grid;
  gap: var(--space-6);
}

.overview-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(18rem, 0.65fr);
  gap: var(--space-6);
}

.workspace-card {
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: var(--radius-surface);
  padding: var(--space-6);
  background: var(--surface);
}

.workspace-card > :deep(.section-header) {
  margin-bottom: var(--space-4);
}

.attention-card {
  border-top: 3px solid var(--accent);
}

.review-list {
  display: grid;
}

.review-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  border-bottom: 1px solid var(--border);
  padding: var(--space-3) 0;
}

.review-item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.review-item div {
  display: grid;
  gap: var(--space-1);
}

.review-item span,
.class-card span,
.class-summary span {
  color: var(--ink-muted);
  font-size: 0.875rem;
}

.status-distribution {
  display: grid;
  margin: 0;
}

.status-distribution div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  border-bottom: 1px solid var(--border);
  padding: var(--space-2) 0;
}

.status-distribution div:last-child {
  border-bottom: 0;
}

.status-distribution dt,
.status-distribution dd {
  margin: 0;
}

.status-distribution dd {
  font-family: var(--font-academic);
  font-size: 1.25rem;
}

.list-toolbar {
  display: flex;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
}

.list-toolbar .ui-input {
  flex: 1;
}

.filter-select {
  width: auto;
  min-width: 11rem;
}

.table-scroll {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
}

.ui-table {
  min-width: 48rem;
}

.ui-table th:first-child,
.ui-table td:first-child {
  padding-left: 0;
}

.ui-table th:last-child,
.ui-table td:last-child {
  padding-right: 0;
  text-align: right;
}

.ui-table tbody tr:last-child td {
  border-bottom: 0;
}

.assignments-table {
  min-width: 64rem;
}

.grades-table {
  min-width: 72rem;
}

.compact-button {
  min-height: 2rem;
  padding: var(--space-1) var(--space-2);
  font-size: 0.8125rem;
}

.class-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
  gap: var(--space-4);
}

.class-card {
  display: grid;
  gap: var(--space-3);
  border: 1px solid var(--border);
  border-radius: var(--radius-surface);
  padding: var(--space-4);
  background: var(--surface-muted);
  color: var(--ink);
  text-align: left;
}

.class-card:hover,
.class-card:focus-visible {
  border-color: var(--accent);
  background: var(--surface);
}

.class-card strong {
  font-family: var(--font-academic);
  font-size: 1.2rem;
}

.class-card__topline {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
}

.class-card__programme {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.class-summary {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  border-left: 3px solid var(--accent);
}

.class-summary p,
.class-summary h2 {
  margin: 0;
}

.class-summary p,
.publication-sheet__intro p {
  color: var(--accent-strong);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.class-summary h2 {
  margin: var(--space-1) 0 var(--space-2);
  font-family: var(--font-academic);
  font-size: 1.5rem;
}

.class-tabs {
  display: flex;
  gap: var(--space-1);
  overflow-x: auto;
  border-bottom: 1px solid var(--border);
}

.class-tabs button {
  min-height: 2.75rem;
  border: 0;
  border-bottom: 3px solid transparent;
  padding: var(--space-2) var(--space-4);
  background: transparent;
  color: var(--ink-muted);
  font-weight: 700;
  white-space: nowrap;
}

.class-tabs button:hover,
.class-tabs button:focus-visible,
.class-tabs button.is-active {
  border-bottom-color: var(--accent);
  color: var(--accent-strong);
}

.publication-sheet {
  margin-bottom: var(--space-6);
  border: 1px solid var(--border);
  border-radius: var(--radius-surface);
  padding: var(--space-6);
  background: var(--surface-muted);
}

.publication-sheet__intro {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
  border-bottom: 1px solid var(--border);
  padding-bottom: var(--space-4);
}

.publication-sheet__intro p,
.publication-sheet__intro h3 {
  margin: 0;
}

.publication-sheet__intro h3 {
  margin-top: var(--space-1);
  font-family: var(--font-academic);
  font-size: 1.25rem;
}

.publication-sheet__intro > span {
  max-width: 20rem;
  color: var(--ink-muted);
  font-size: 0.8125rem;
  text-align: right;
}

.form-card {
  max-width: 64rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-6);
}

.form-wide,
.form-actions {
  grid-column: 1 / -1;
}

.form-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  padding-top: var(--space-2);
}

.task-brief {
  min-height: 11rem;
}

.multi-select {
  min-height: 10rem;
}

.grades-toolbar {
  flex-wrap: wrap;
}

.export-button {
  flex: none;
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.feedback-column {
  max-width: 20rem;
}

@media (max-width: 960px) {
  .overview-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 767px) {
  .workspace-card,
  .publication-sheet {
    padding: var(--space-4);
  }

  .compact-button {
    min-width: 44px;
    min-height: 44px;
  }

  .list-toolbar,
  .form-grid,
  .publication-sheet__intro {
    grid-template-columns: minmax(0, 1fr);
    flex-direction: column;
  }

  .filter-select {
    width: 100%;
  }

  .form-wide,
  .form-actions {
    grid-column: auto;
  }

  .publication-sheet__intro > span {
    max-width: none;
    text-align: left;
  }

  .review-item,
  .class-summary {
    align-items: stretch;
    flex-direction: column;
  }

  .class-tabs button,
  .export-button {
    min-height: 44px;
  }

  .table-scroll {
    overflow: visible;
  }

  .ui-table,
  .ui-table tbody {
    display: block;
    width: 100%;
    min-width: 0;
    background: transparent;
  }

  .ui-table thead {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .ui-table tbody tr {
    display: grid;
    width: 100%;
    gap: 0;
    margin-bottom: var(--space-3);
    border: 1px solid var(--border);
    border-left: 3px solid var(--accent);
    border-radius: var(--radius-surface);
    padding: var(--space-3);
    background: var(--surface);
  }

  .ui-table tbody tr:last-child {
    margin-bottom: 0;
  }

  .ui-table td,
  .ui-table th:first-child,
  .ui-table td:first-child,
  .ui-table th:last-child,
  .ui-table td:last-child {
    display: grid;
    grid-template-columns: minmax(6.5rem, 0.4fr) minmax(0, 1fr);
    align-items: start;
    gap: var(--space-3);
    border: 0;
    padding: var(--space-2) 0;
    text-align: left;
    white-space: normal;
  }

  .ui-table td::before {
    color: var(--ink-muted);
    content: attr(data-label);
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
  }

  .assignment-note-editor td {
    grid-template-columns: minmax(0, 1fr);
  }

  .note-editor-controls {
    display: grid;
    grid-column: 1 / -1;
    gap: var(--space-3);
    min-width: 0;
  }

  .feedback-column {
    max-width: none;
  }
}
</style>
